import { format, parseISO } from 'date-fns'
import { toZonedTime, fromZonedTime } from 'date-fns-tz'

const FALLBACK_TZ = 'UTC'

function resolveTz(timezone: string | null | undefined): string {
  return timezone || FALLBACK_TZ
}

/**
 * Converts a UTC ISO string to the given IANA timezone and formats it.
 */
export function formatDateInTz(
  isoString: string,
  timezone: string | null | undefined,
  formatStr: string,
): string {
  const tz = resolveTz(timezone)
  const zonedDate = toZonedTime(isoString, tz)
  return format(zonedDate, formatStr)
}

/**
 * Converts a UTC ISO string to a `datetime-local` input value in the property's timezone.
 * Returns a string like "2024-01-15T22:00" suitable for <input type="datetime-local">.
 */
export function toPropertyTzDatetimeLocal(
  isoString: string,
  timezone: string | null | undefined,
): string {
  return formatDateInTz(isoString, timezone, "yyyy-MM-dd'T'HH:mm")
}

/**
 * Interprets a `datetime-local` string as the property's timezone and returns a UTC ISO string.
 */
export function fromPropertyTzToUTC(
  datetimeLocalStr: string,
  timezone: string | null | undefined,
): string {
  const tz = resolveTz(timezone)
  const utcDate = fromZonedTime(datetimeLocalStr, tz)
  return utcDate.toISOString()
}

/**
 * Replaces the time of a `datetime-local` value with `time` ("HH:mm" or "HH:mm:ss").
 */
export function withTimeOfDay(datetimeLocalStr: string, time: string): string {
  return `${datetimeLocalStr.slice(0, 10)}T${time.slice(0, 5)}`
}

/**
 * Formats a date-only value (`YYYY-MM-DD`, e.g. an expense date) for display.
 * `new Date('2026-10-30')` is UTC midnight, which is the previous day west of
 * UTC; `parseISO` reads a date-only string as local midnight instead.
 */
export function formatDateOnly(value: string | null | undefined): string {
  if (!value) return ''
  return parseISO(value).toLocaleDateString()
}
